import React, { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';

/**
 * Global Network Status Bar
 * ---------------------------------------------------------------
 * Mounted ONCE at the App root. Listens to the browser's native
 * `online` / `offline` events (window.navigator.onLine) and
 * renders a prominent amber bar pinned to the top of the viewport
 * whenever the connection drops. Auto-hides on reconnect.
 *
 * Side-effect: while offline the class `chanakya-offline` is
 * added to <body>. A global CSS rule in `index.css` disables
 * every Save / Confirm / Delete button on the app the instant
 * that class appears — no per-page changes required. A belt-and-
 * braces guard in `/lib/api.js` also rejects action-API network
 * calls while offline so operators cannot lose data even if a
 * button somehow slips through.
 *
 * Lightweight: no polling, no third-party lib, no re-render loop.
 */
export default function NetworkStatusBar() {
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  useEffect(() => {
    const goOnline  = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Sync a body class so global CSS can disable save/confirm buttons
  // everywhere the moment the network drops.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (online) document.body.classList.remove('chanakya-offline');
    else        document.body.classList.add('chanakya-offline');
    return () => document.body.classList.remove('chanakya-offline');
  }, [online]);

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="network-status-bar"
      className="fixed inset-x-0 top-0 z-[9999] flex items-center justify-center gap-2 border-b border-amber-600/70 bg-amber-500/95 px-4 py-2 text-[13px] font-medium text-amber-950 shadow-md backdrop-blur-sm dark:bg-amber-500/90"
    >
      <WifiOff className="h-4 w-4" aria-hidden />
      <span>
        No internet connection — Save &amp; Confirm actions are disabled.
        The bar will disappear once the connection is restored.
      </span>
    </div>
  );
}
